import React from 'react';
import { useMsal } from '@azure/msal-react';
import Configuration from '../../config/einvoicing-configuration';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { ErrorState } from '../common/ErrorState';
import { Loading } from '../common/Loading';
import {
    Table,
    TableHeader,
    TableRow,
    TableBody,
    TableCell,
    TableHeaderCell,
    OverlayDrawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    DrawerFooter,
    TabList,
    Tab,
    Dropdown,
    Option,
    makeStyles,
    shorthands,
    Button,
    Switch,
    Tooltip,
    Label,
    tokens,
    SelectionEvents,
    OptionOnSelectData,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogTrigger,
    MessageBar,
    MessageBarBody
} from '@fluentui/react-components';
import {
    ArrowDownloadRegular,
    DataUsageRegular,
    ErrorCircleRegular,
    ErrorCircleFilled,
    CheckmarkCircleFilled,
    ChevronDownRegular,
    ChevronRightRegular,
    ArrowSyncRegular
} from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import { ColumnExtended } from "../../pages/dashboard/ColumnExtended";
import { DataGridProps } from "./DataGridProps";
import { constructGetAnalysisUrl, constructGetDocumentBlobsUrl, constructGetDocumentEventsUrl, constructGetRequest, constructResendPartnerResponseUrl } from "../../controller/dashboardController";
import { FriendlyCountryNameToCountryCodeMapping, FriendlyNameToModeMapping, FriendlyPartnerNameToPartnerIdMapping, ScenarioToFriendlyNameMapping, ErrorCodeandSubErrorCodeFriendlyNameMapping, StringBoolDictionary } from "../../utils/FriendlyNameMappings";
import ErrorBoundary from "../common/ErrorBoundary";
import "./DataGrid.css";

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '70vh',
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
    },
    cellContent: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        maxWidth: '100%',
        color: tokens.colorNeutralForeground1,
    },
    activityItem: {
        display: 'flex',
        ...shorthands.gap('10px'),
        marginBottom: '15px',
        marginTop: '10px',
    },
    activityIcon: {
        display: 'flex',
        alignItems: 'start',
        paddingTop: '2px',
    },
    activityContent: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('4px'),
    },
    activityDescription: {
        fontWeight: 'bold',
    },
    activityComments: {
        color: tokens.colorNeutralForeground1,
    },
    activityTimeStamp: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground1,
    },
    pageOptions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '10px',
        paddingBottom: '10px',
        paddingLeft: '20px',
        paddingRight: '20px',
    },
    paginationControls: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('10px'),
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('10px'),
    },
    label: {
        marginTop: '10px',
        fontWeight: 'bold',
        display: 'block',
    },
    resizer: {
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '24px',
        cursor: 'col-resize',
        zIndex: 1,
        ':hover': {
            backgroundColor: tokens.colorNeutralStroke1,
        },
        ':focus-visible': {
            backgroundColor: tokens.colorNeutralStroke1,
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorStrokeFocus2,
        },
    },
    headerCell: {
        backgroundColor: tokens.colorNeutralBackground2,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
    },
    row: {
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        ':focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorBrandStroke1,
            outlineOffset: '-2px',
        },
    },
    rowStriped: {
        backgroundColor: tokens.colorNeutralBackground2,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
        },
        ':focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorBrandStroke1,
            outlineOffset: '-2px',
        },
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
        boxShadow: tokens.shadow4,
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    drawerBody: {
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.padding(tokens.spacingVerticalM),
    }
});

function DataGrid(props: DataGridProps) {
    const { instance, accounts } = useMsal();
    const styles = useStyles();
    const [selectedItem, setSelectedItem] = React.useState<any>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [documentEvents, setDocumentEvents] = React.useState<any>(null);
    const [showPriorityOnly, setShowPriorityOnly] = React.useState(true);
    const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = React.useState(false);
    const [failureAnalysisData, setFailureAnalysisData] = React.useState<any>(null);
    const [selectedRequestIndex, setSelectedRequestIndex] = React.useState(0);
    const [accessToken, setAccessToken] = React.useState('');
    const [isError, setIsError] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedScenario, setSelectedScenario] = React.useState<string>('');
    const [columns, setColumns] = React.useState<ColumnExtended[]>(props.columns);
    const [isAnalysisLoading, setIsAnalysisLoading] = React.useState(false);
    const [isResending, setIsResending] = React.useState(false);
    const [resendStatus, setResendStatus] = React.useState<string | null>(null);
    const [isResendDialogOpen, setIsResendDialogOpen] = React.useState(false);
    const [pendingResendRequest, setPendingResendRequest] = React.useState<any>(null);
    const resizingRef = React.useRef<{ index: number; startX: number; startWidth: number } | null>(null);

    const onMouseMove = React.useCallback((e: MouseEvent) => {
        if (!resizingRef.current) return;
        const { index, startX, startWidth } = resizingRef.current;
        const diff = e.clientX - startX;
        const newWidth = Math.max(50, startWidth + diff);

        setColumns(prev => {
            const next = [...prev];
            next[index] = { ...next[index], minWidth: newWidth };
            return next;
        });
    }, []);

    // WCAG 2.5.2 Pointer Cancellation: Escape key cancels drag and restores original width
    const onKeyDownDuringResize = React.useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape' && resizingRef.current) {
            const { index, startWidth } = resizingRef.current;
            setColumns(prev => {
                const next = [...prev];
                next[index] = { ...next[index], minWidth: startWidth };
                return next;
            });
            resizingRef.current = null;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('keydown', onKeyDownDuringResize);
        }
    }, []);

    const onMouseUp = React.useCallback(() => {
        resizingRef.current = null;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('keydown', onKeyDownDuringResize);
    }, [onMouseMove, onKeyDownDuringResize]);

    const onResizeStart = (e: React.MouseEvent, index: number, column: ColumnExtended) => {
        e.preventDefault();
        e.stopPropagation();
        resizingRef.current = {
            index,
            startX: e.clientX,
            startWidth: column.minWidth || 100
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('keydown', onKeyDownDuringResize);
    };

    // WCAG 2.5.1 Pointer Gestures: Keyboard alternative for column resizing
    const onResizerKeyDown = (e: React.KeyboardEvent, index: number, column: ColumnExtended) => {
        const step = e.shiftKey ? 50 : 10;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            const currentWidth = column.minWidth || 100;
            const delta = e.key === 'ArrowRight' ? step : -step;
            const newWidth = Math.max(50, currentWidth + delta);
            setColumns(prev => {
                const next = [...prev];
                next[index] = { ...next[index], minWidth: newWidth };
                return next;
            });
        }
    };

    const previousFocusRef = React.useRef<HTMLElement | null>(null);
    const analysisTriggerRef = React.useRef<HTMLElement | null>(null);

    const onRowClick = (item: any) => {
        previousFocusRef.current = document.activeElement as HTMLElement;
        if (item && item !== null) {
            GetDocumentEvents(item.documentId, item.countryCode, item.eInvPartnerId, item.mode);
        }
        setSelectedItem(item);
        setIsOpen(true);
    };

    const onDismiss = () => {
        setSelectedItem(null);
        setIsOpen(false);
        setDocumentEvents(null);
        setShowPriorityOnly(true);
        setIsAnalysisPanelOpen(false);
        setFailureAnalysisData(null);
        setSelectedRequestIndex(0);
        requestAnimationFrame(() => {
            previousFocusRef.current?.focus();
        });
    };

    const onAnalysisPanelDismiss = () => {
        setIsAnalysisPanelOpen(false);
        setFailureAnalysisData(null);
        setIsOpen(true);
        requestAnimationFrame(() => {
            analysisTriggerRef.current?.focus();
        });
    };

    const onAnalysisButtonClick = (failedRequest: any, triggerElement?: HTMLElement) => {
        analysisTriggerRef.current = triggerElement || document.activeElement as HTMLElement;
        getFailureAnalysis(selectedItem.documentId, failedRequest.requestId);
        setIsOpen(false);
        setIsAnalysisPanelOpen(true);
    };

    async function onResendResponse(failedRequest: any) {
        if (!selectedItem || !failedRequest) return;
        setIsResending(true);
        setResendStatus(null);
        try {
            const resendUrl = constructResendPartnerResponseUrl(
                Configuration.eventServiceUrl,
                selectedItem.documentId,
                failedRequest.supplierId || selectedItem.supplierId || '',
                selectedItem.mode,
                selectedScenario,
                selectedItem.countryCode,
                selectedItem.eInvPartnerId,
                true // triggerReprocessing
            );
            const requestParams = constructGetRequest(await getAccessToken());
            const result = await fetch(resendUrl, requestParams);
            if (result.ok) {
                setResendStatus('Resend request queued successfully.');
            } else {
                const errorText = await result.text();
                setResendStatus('Failed to queue resend: ' + errorText);
            }
        } catch (err) {
            setResendStatus('Failed to queue resend.');
        } finally {
            setIsResending(false);
        }
    }

    async function getAccessToken() {
        if (accessToken === '') {
            const request = {
                ...Configuration.msalTokenRequest,
                account: accounts[0]
            };
            try {
                const result: AuthenticationResult = await instance.acquireTokenSilent(request);
                setAccessToken(result.idToken);
                return result.idToken;
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    await instance.acquireTokenRedirect(request);
                    return '';
                }
                throw error;
            }
        }
        return accessToken;
    }

    function getScenariosFromDocuments(documents: any[]) {
        return documents.map(a => ({ key: a.scenario, text: ScenarioToFriendlyNameMapping[a.scenario] }));
    }

    function getExpectationStatus(expectations: StringBoolDictionary): string {
        if (expectations['HighConfidence'] === false) {
            return "Outside expected time range";
        }
        if (expectations['MediumConfidence'] === false) {
            return "Delayed";
        }
        if (expectations['LowConfidence'] === false) {
            return "Within expected time range";
        }
        return "Completed in Time";
    }

    function GetDocumentEvents(documentId: string, countryCode: string, partnerId: string, mode: string) {
        const searchUrl: string = constructGetDocumentEventsUrl(Configuration.eventServiceUrl, documentId, FriendlyCountryNameToCountryCodeMapping[countryCode], FriendlyPartnerNameToPartnerIdMapping[partnerId], FriendlyNameToModeMapping[mode]);
        async function startDocumentSearch() {
            setIsError(false);
            setIsLoading(true);
            try {
                const requestParams = constructGetRequest(await getAccessToken());
                const result = await fetch(searchUrl, requestParams);
                if (result !== undefined && result.ok) {
                    const eventsData = await result.json();
                    setDocumentEvents(eventsData);
                    if (eventsData && eventsData.length > 0) {
                        setSelectedScenario(eventsData[0].scenario);
                    }
                    setIsLoading(false);
                }
                else {
                    setIsLoading(false);
                    setIsError(true);
                }
            }
            catch {
                setIsLoading(false);
                setIsError(true);
            }
        }
        startDocumentSearch();
    }

    function onToggleChange(ev: React.ChangeEvent<HTMLInputElement>) {
        setShowPriorityOnly(ev.target.checked)
    }

    async function GetDocumentData() {
        const searchUrl: string = constructGetDocumentBlobsUrl(Configuration.eventServiceUrl, selectedItem.documentId, FriendlyCountryNameToCountryCodeMapping[selectedItem.countryCode], FriendlyPartnerNameToPartnerIdMapping[selectedItem.eInvPartnerId], selectedScenario, FriendlyNameToModeMapping[selectedItem.mode]);
        setIsError(false);
        setIsLoading(true);
        try {
            const requestParams = constructGetRequest(await getAccessToken());
            const result = await fetch(searchUrl, requestParams);
            if (result !== undefined && result.ok) {
                var blob = await result.blob();
                saveAs(blob, `${selectedItem.documentId}.zip`);
                setIsLoading(false);
            }
            else {
                setIsLoading(false);
            }
        }
        catch {
            setIsLoading(false);
        }
    }

    function getErrorCodeDescription(item: string) {
        if (!item) return "";
        var errorCodes: string[] = item.split(',').filter(code => code.trim() !== '');
        if (errorCodes.length === 0) return "";
        var errorDescription: string = "";
        for (let i = 0; i < errorCodes.length; i++) {
            const description = ErrorCodeandSubErrorCodeFriendlyNameMapping[errorCodes[i]];
            errorDescription = errorDescription + errorCodes[i] + (description ? ": " + description : "");
            if (i !== errorCodes.length - 1) {
                errorDescription = errorDescription + ", ";
            }
        }
        return errorDescription;
    }

    function onRenderFooterContent() {
        const failedRequests = documentEvents
            ? documentEvents
                .filter((a: { scenario: string; status: string; hasLLMAnalysisUri: boolean; }) =>
                    a.scenario === selectedScenario && a.status === 'failed' && a.hasLLMAnalysisUri)
                .sort((a: { startEventTime: string }, b: { startEventTime: string }) =>
                    new Date(b.startEventTime).getTime() - new Date(a.startEventTime).getTime())
            : [];

        const failedRequest = failedRequests.length > 0 ? failedRequests[0] : null;

        return (
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                <Button onClick={GetDocumentData} icon={<ArrowDownloadRegular />}>
                    Download Data
                </Button>
                {failedRequest && (
                    <>
                        <Button
                            onClick={(e) => onAnalysisButtonClick(failedRequest, e.currentTarget as HTMLElement)}
                            icon={<DataUsageRegular />}
                            disabled={isAnalysisLoading}
                            aria-disabled={isAnalysisLoading}
                            aria-label={isAnalysisPanelOpen ? 'Analysis Open' : 'View Failure Analysis'}
                            aria-description="Open the AI-powered failure analysis panel for this document"
                        >
                            {isAnalysisPanelOpen ? 'Analysis Open' : 'View Failure Analysis'}
                        </Button>
                    </>
                )}
                <Button
                    onClick={() => {
                        setPendingResendRequest(failedRequest);
                        setIsResendDialogOpen(true);
                    }}
                    icon={<ArrowSyncRegular />}
                    disabled={isResending}
                    aria-description="Opens a confirmation dialog before resending the response" >
                    {isResending ? 'Resending...' : 'Resend Response'}
                </Button>
                {resendStatus && (
                        <span role="status" aria-live="polite" style={{ marginLeft: 10, display: 'inline-flex', alignItems: 'center', gap: 4, color: resendStatus.toLowerCase().includes('success') || resendStatus.toLowerCase().includes('queued') ? '#0B6A0B' : '#A80000' }}>
                        {resendStatus.toLowerCase().includes('success') || resendStatus.toLowerCase().includes('queued')
                            ? <CheckmarkCircleFilled aria-hidden="true" />
                            : <ErrorCircleFilled aria-hidden="true" />}
                        {resendStatus}
                    </span>
                )}
            </div>
        );
    }

    function buildActivityItems(events: any[]) {
        var items: any[] = []
        events.forEach(function (event: any, index: number) {
            if (!showPriorityOnly || event.hasPriorityDisplay) {
                items.push(
                    {
                        key: index,
                        activityDescription: <span> {event.eventName}</span>,
                        activityIcon: <DataUsageRegular />,
                        comments: <span>{event.displayContent}</span>,
                        timeStamp: event.eventTimeStamp,
                    },
                )
            }
        })
        return items
    }

    const onDropdownChange = (event: SelectionEvents, data: OptionOnSelectData): void => {
        if (data.optionValue) {
            setSelectedScenario(data.optionValue);
        }
    };

    function getPanelHTML() {
        if (isLoading) {
            return (<Loading label='Loading Data...' />);
        }
        else if (!isError && documentEvents) {
            const filteredRequests = documentEvents.filter((a: { scenario: string; }) => a.scenario === selectedScenario);
            return (
                <div className={styles.drawerContent}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <Switch label="Display Priority Events Only" defaultChecked onChange={onToggleChange} aria-description="Toggle to show only high-priority events or all events" />
                    </div>
                    {documentEvents.length > 1 && (
                        <div style={{ marginBottom: '10px' }}>
                            <Label id="scenario-label">Scenario</Label>
                            <Dropdown
                                aria-labelledby="scenario-dropdown-label"
                                aria-haspopup="listbox"
                                onOptionSelect={onDropdownChange}
                                value={ScenarioToFriendlyNameMapping[selectedScenario] || selectedScenario}
                                selectedOptions={[selectedScenario]}
                            >
                                {getScenariosFromDocuments(documentEvents).map((option) => (
                                    <Option key={option.key} value={option.key}>
                                        {option.text}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                    )}
                    <TabList
                        selectedValue={selectedRequestIndex}
                        onTabSelect={(_, data) => setSelectedRequestIndex(data.value as number)}
                        aria-label="Request submissions"
                    >
                        {filteredRequests.map((request: any, index: number) => (
                            <Tab key={index} value={index}>Request - {index + 1}</Tab>
                        ))}
                    </TabList>
                    {filteredRequests.map((request: any, index: number) => (
                        selectedRequestIndex === index && (
                            <div key={index} style={{ marginTop: '10px' }}>
                                <Label className={styles.label}>Request Id - {request.requestId}</Label>
                                {request.expectations !== null && request.expectations !== undefined && Object.keys(request.expectations).length !== 0 && (
                                    <Label className={styles.label}>Expectation Status - {getExpectationStatus(request.expectations)}</Label>
                                )}
                                <div style={{ marginTop: '15px' }}>
                                    {buildActivityItems(request.eInvoicingEvents).map((item: any) => (
                                        <div key={item.key} className={styles.activityItem}>
                                            <div className={styles.activityIcon}>{item.activityIcon}</div>
                                            <div className={styles.activityContent}>
                                                <div className={styles.activityDescription}>{item.activityDescription}</div>
                                                <div className={styles.activityComments}>{item.comments}</div>
                                                <div className={styles.activityTimeStamp}>{item.timeStamp}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </div>);
        }
        else {
            return (<ErrorState
                description="Check your internet connection, try refreshing the page or contact support."
                title="There was an issue loading the Document events"
            />);
        }
    }

    const onColumnHeaderClick = (ev?: React.MouseEvent<HTMLElement>, column?: ColumnExtended) => {
        if (column && column.isSortable) {
            const newColumns = [...columns];
            const currColumn = newColumns.find(currCol => column.key === currCol.key);
            if (currColumn) {
                const currColumnIndex = newColumns.indexOf(currColumn);
                newColumns[currColumnIndex] = {
                    ...newColumns[currColumnIndex],
                    isSorted: true,
                    isSortedDescending: !currColumn.isSortedDescending,
                };
                newColumns.forEach((newCol, index) => {
                    if (index !== currColumnIndex) {
                        newCol.isSorted = false;
                        newCol.isSortedDescending = true;
                    }
                });
                setColumns(newColumns);
                props.setSearchParams({ ...props.searchParams, orderBy: currColumn.fieldName, desc: !currColumn.isSortedDescending, page: 0 })
            }
        }
    };

    const onPageChange = (page: number) => {
        props.setSearchParams({ ...props.searchParams, page: page })
    };

    function getPageSizeOptions() {
        return [
            { key: 10, text: "10", data: 10 },
            { key: 25, text: "25", data: 25 },
            { key: 50, text: "50", data: 50 },
            { key: 100, text: "100", data: 100 }
        ]
    }

    function getFailureAnalysis(documentId: string, requestId: string) {
        setIsAnalysisLoading(true);
        async function startAnalysisSearch() {
            try {
                // Construct URL to fetch LLM analysis for this document
                const analysisUrl = constructGetAnalysisUrl(Configuration.eventServiceUrl, documentId, requestId);
                const requestParams = constructGetRequest(await getAccessToken());
                const result = await fetch(analysisUrl, requestParams);



                if (result !== undefined && result.ok) {
                    const analysisText = await result.text();
                    let parsed: any = null;
                    let isStructured = false;
                    try {
                        // Trim any whitespace before parsing
                        const trimmedText = analysisText.trim();
                        parsed = JSON.parse(trimmedText);

                        // Validate that the parsed object has the expected structure
                        isStructured = parsed && typeof parsed === 'object' &&
                            ('summary' in parsed) && ('details' in parsed) &&
                            ('errorAnalysis' in parsed) && ('resolution' in parsed) &&
                            ('prevention' in parsed);

                        // Ensure all fields are properly formatted
                        if (isStructured) {
                            const keys = ['details', 'summary', 'errorAnalysis', 'resolution', 'prevention'];
                            keys.forEach(key => {
                                if (typeof parsed[key] !== 'object') {
                                    parsed[key] = parsed[key] || '';
                                }
                            });
                        }
                    } catch (e) {
                        console.error("Error parsing analysis JSON:", e);
                        isStructured = false;
                    }

                    setFailureAnalysisData({
                        requestId,
                        isStructured,
                        parsed,
                        analysisText
                    });
                    setIsAnalysisLoading(false);
                } else {
                    setIsAnalysisLoading(false);
                    setFailureAnalysisData({
                        requestId,
                        error: "Failed to retrieve analysis data"
                    });
                }
            } catch (error) {
                console.error("Error fetching failure analysis:", error);
                setIsAnalysisLoading(false);
                setFailureAnalysisData({
                    requestId,
                    error: "An error occurred while retrieving analysis data"
                });
            }
        }
        startAnalysisSearch();
    }


    function renderFailureAnalysis(data: any) {
        if (data.error) {
            return (
                <div className="failure-analysis-error">
                    <ErrorCircleRegular style={{ color: '#A80000', marginRight: '8px' }} />
                    <span>{data.error}</span>
                </div>
            );
        }

        // Structured JSON format (new)
        if (data.isStructured && data.parsed) {
            // Safely extract values from parsed data
            const summary = data.parsed.summary || '';
            const details = typeof data.parsed.details === 'object' ? JSON.stringify(data.parsed.details, null, 2) : (data.parsed.details || '');
            const errorAnalysis = data.parsed.errorAnalysis || '';
            const resolution = data.parsed.resolution || '';
            const prevention = data.parsed.prevention || '';

            // Collapsible section component
            const CollapsibleSection = ({ title, children, defaultOpen = false }: { title: string, children: React.ReactNode, defaultOpen?: boolean }) => {
                const [open, setOpen] = React.useState(defaultOpen);
                return (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 10, background: '#fafbfc' }}>
                        <div
                            style={{ cursor: 'pointer', padding: '10px 16px', fontWeight: 500, display: 'flex', alignItems: 'center', userSelect: 'none', color: '#242424' }}
                            onClick={() => setOpen(!open)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}
                            role="button"
                            tabIndex={0}
                            aria-expanded={open}
                        >
                            {open ? <ChevronDownRegular style={{ marginRight: 8, fontSize: 14 }} /> : <ChevronRightRegular style={{ marginRight: 8, fontSize: 14 }} />}
                            {title}
                        </div>
                        {open && (
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', background: '#fff', whiteSpace: 'pre-wrap', color: '#242424' }}>
                                {children}
                            </div>
                        )}
                    </div>
                );
            };

            return (
                <div className="failure-analysis-details">
                    <Label className={styles.label}>LLM Analysis for Request ID: {data.requestId}</Label>
                    <div className="analysis-content llm-content" style={{ marginTop: 8 }}>
                        <CollapsibleSection title="Summary" defaultOpen={true}>
                            {typeof data.parsed.summary === 'object' ? (
                                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                                    {JSON.stringify(data.parsed.summary, null, 2)}
                                </pre>
                            ) : (
                                summary || 'No summary available.'
                            )}
                        </CollapsibleSection>
                        <CollapsibleSection title="Details">
                            {typeof data.parsed.details === 'object' ? (
                                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                                    {JSON.stringify(data.parsed.details, null, 2)}
                                </pre>
                            ) : (
                                details || 'No details available.'
                            )}
                        </CollapsibleSection>
                        <CollapsibleSection title="Error Analysis">
                            {typeof data.parsed.errorAnalysis === 'object' ? (
                                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                                    {JSON.stringify(data.parsed.errorAnalysis, null, 2)}
                                </pre>
                            ) : (
                                errorAnalysis || 'No error analysis available.'
                            )}
                        </CollapsibleSection>
                        <CollapsibleSection title="Resolution">
                            {typeof data.parsed.resolution === 'object' ? (
                                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                                    {JSON.stringify(data.parsed.resolution, null, 2)}
                                </pre>
                            ) : (
                                resolution || 'No resolution available.'
                            )}
                        </CollapsibleSection>
                        <CollapsibleSection title="Prevention">
                            {typeof data.parsed.prevention === 'object' ? (
                                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                                    {JSON.stringify(data.parsed.prevention, null, 2)}
                                </pre>
                            ) : (
                                prevention || 'No prevention measures available.'
                            )}
                        </CollapsibleSection>
                    </div>
                </div>
            );
        }

        // Fallback: legacy wall of text
        return (
            <div className="failure-analysis-details">
                <Label className={styles.label}>LLM Analysis for Request ID: {data.requestId}</Label>
                <div className="analysis-content llm-content">
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {data.analysisText || "No analysis available"}
                    </pre>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.tableContainer} tabIndex={0} role="region" aria-label="Document search results">
                <Table style={{ minWidth: '100%', tableLayout: 'fixed', width: 'max-content' }}>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column, index) => (
                                <TableHeaderCell
                                    key={column.key}
                                    onClick={() => onColumnHeaderClick(undefined, column)}
                                    sortDirection={column.isSorted ? (column.isSortedDescending ? 'descending' : 'ascending') : undefined}
                                    style={{ minWidth: column.minWidth, position: 'relative' }}
                                    className={styles.headerCell}
                                >
                                    {column.name}
                                    {column.isResizable && (
                                        <div
                                            className={styles.resizer}
                                            role="separator"
                                            aria-orientation="vertical"
                                            aria-label={`Resize column ${column.name}`}
                                            title="Use left and right arrow keys to resize, hold Shift for larger steps. Press Escape during mouse drag to cancel."
                                            tabIndex={0}
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => onResizeStart(e, index, column)}
                                            onKeyDown={(e) => onResizerKeyDown(e, index, column)}
                                        />
                                    )}
                                </TableHeaderCell>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {props.data.map((item, index) => (
                            <TableRow
                                key={index}
                                onClick={() => onRowClick(item)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(item); } }}
                                tabIndex={0}
                                style={{ cursor: 'pointer', scrollMargin: 8 }}
                                className={index % 2 === 0 ? styles.row : styles.rowStriped}
                                aria-label={`Document ${item.documentId || ''}, Status ${item.status || ''}`}
                            >
                                {columns.map(column => (
                                    <TableCell key={column.key} style={{ minWidth: column.minWidth, maxWidth: column.minWidth }}>
                                        {column.onRender ? column.onRender(item, index, column) : (
                                            column.key === 'errorCode' ? (
                                                <Tooltip content={item.errorCodeDescription || getErrorCodeDescription(item[column.fieldName || '']) || 'Error code details'} relationship="description">
                                                    <span className={styles.cellContent}>{item[column.fieldName || '']}</span>
                                                </Tooltip>
                                            ) : (
                                                <span className={styles.cellContent}>{item[column.fieldName || '']}</span>
                                            )
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <div className={styles.pageOptions}>
                <div className={styles.paginationControls}>
                    <Button onClick={() => onPageChange(props.searchParams.page - 1)} disabled={props.searchParams.page === 0} aria-disabled={props.searchParams.page === 0} aria-label="Previous page" aria-description="Go to the previous page of results">
                        Previous
                    </Button>
                    <strong aria-live="polite" role="status">Page: {props.searchParams.page + 1}/{Math.ceil(props.totalCount / props.searchParams.pageSize)}</strong>
                    <Button onClick={() => onPageChange(props.searchParams.page + 1)} disabled={props.totalCount < (props.searchParams.page + 1) * props.searchParams.pageSize} aria-disabled={props.totalCount < (props.searchParams.page + 1) * props.searchParams.pageSize} aria-label="Next page" aria-description="Go to the next page of results">
                        Next
                    </Button>
                </div>
                <div>
                    <Dropdown
                        aria-label="Items per page"
                        aria-description="Select the number of documents to display per page"
                        aria-haspopup="listbox"
                        placeholder={`${props.searchParams.pageSize}`}
                        onOptionSelect={(_, data) => {
                            if (data.optionValue) {
                                props.setSearchParams({ ...props.searchParams, pageSize: parseInt(data.optionValue) });
                            }
                        }}
                        value={`${props.searchParams.pageSize}`} >
                        {getPageSizeOptions().map(opt => (
                            <Option key={opt.key} value={opt.key.toString()}>
                                {opt.text}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
            </div>

            <OverlayDrawer
                open={isOpen}
                onOpenChange={(_, { open }) => !open && onDismiss()}
                position="end"
                size="medium"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle>Document ID - {selectedItem?.documentId}</DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody className={styles.drawerBody} tabIndex={0} aria-label="Document event details">
                    <div className={styles.card}>
                        {getPanelHTML()}
                    </div>
                </DrawerBody>
                <DrawerFooter>
                    {onRenderFooterContent()}
                </DrawerFooter>
            </OverlayDrawer>

            <Dialog open={isResendDialogOpen} onOpenChange={(_, data) => setIsResendDialogOpen(data.open)}>
                <DialogSurface aria-label="Confirm resend response">
                    <DialogBody>
                        <DialogTitle>Confirm Resend Response</DialogTitle>
                        <DialogContent>
                            <p>You are about to resend the response for document <strong>{selectedItem?.documentId}</strong>.</p>
                            <p>This action will requeue the document for response processing, which may trigger notifications to the partner. Please review the document details before proceeding.</p>
                            <MessageBar intent="warning" style={{ marginTop: 8 }}>
                                <MessageBarBody>This action modifies data in the processing queue and cannot be undone.</MessageBarBody>
                            </MessageBar>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary" aria-description="Cancel the resend and return to the document details">Cancel</Button>
                            </DialogTrigger>
                            <Button
                                appearance="primary"
                                onClick={() => {
                                    setIsResendDialogOpen(false);
                                    onResendResponse(pendingResendRequest);
                                }}
                                aria-description="Confirm and execute the resend response action"
                            >
                                Confirm Resend
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {isAnalysisPanelOpen && (
                <OverlayDrawer
                    open={isAnalysisPanelOpen}
                    onOpenChange={(_, { open }) => !open && onAnalysisPanelDismiss()}
                    position="end"
                    size="medium"
                >
                    <DrawerHeader>
                        <DrawerHeaderTitle>Failure Analysis - {selectedItem?.documentId}</DrawerHeaderTitle>
                    </DrawerHeader>
                    <DrawerBody className={styles.drawerBody}>
                        <div className={styles.card}>
                            {isAnalysisLoading ? (
                                <Loading label='Analyzing failure...' />
                            ) : failureAnalysisData ? (
                                <div className="failure-analysis-content" style={{
                                    padding: '10px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    color: '#242424'
                                }}>
                                    <ErrorBoundary
                                        errorData={failureAnalysisData}
                                        fallbackContent={
                                            <div className="failure-analysis-error">
                                                <ErrorCircleRegular style={{ color: '#A80000', marginRight: '8px' }} />
                                                <span>Error rendering analysis data. The data may be malformed.</span>
                                                <pre style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px', maxHeight: '200px', overflow: 'auto' }}>
                                                    {typeof failureAnalysisData.analysisText === 'string'
                                                        ? failureAnalysisData.analysisText.substring(0, 500) + (failureAnalysisData.analysisText.length > 500 ? '...' : '')
                                                        : 'Analysis data not available as text'}
                                                </pre>
                                            </div>
                                        }
                                    >
                                        {renderFailureAnalysis(failureAnalysisData)}
                                    </ErrorBoundary>
                                </div>
                            ) : (
                                <div style={{ padding: '10px' }}>
                                    <em>No analysis data available</em>
                                </div>
                            )}
                        </div>
                    </DrawerBody>
                </OverlayDrawer>
            )}
        </div>
    );
}

export default DataGrid;